/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 * 
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 * 
 * Software credits: 
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 * 
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 * 
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Table, Radio, Row, Col, Divider, Spin } from 'antd';

import { MESSAGES } from '@translations/labels';
import DSSearch from '@design-system/search/DSSearch';
import { usePaginationWrapper } from '@hooks/usePaginationWrapper';
import { formatDateTime } from '@utils/format/dates';
import { getCustomNoData } from '@utils/noDataCustom';
import SvgIconCopyPaste from '@svg/IconCopyPaste';
import { ROUTES, idField, NONE_INTENT } from '@constants';

import { useActivity } from '../../../hooks/useActivity';
import { useIntentsSearch } from '../../../../manage-the-bot/hooks/useIntentsSearch';
import { useIntentsTable } from '../../../../manage-the-bot/hooks/useIntentsTable';
import { intentColumns, getNlpEvaluationFromInteraction } from './utilsReassignModalContent';
import ModalStyles from './intentReassignModalContentStyles';

const labels = {
  ...MESSAGES.conversationHistory.modalReassignIntent,
  searchPlaceholder: MESSAGES.manageBot.searchIntentPlaceholder,
};

const IntentReassignContent = ({ settings }) => {
  const { paginationWrapper } = usePaginationWrapper();
  const { searchIntents } = useIntentsSearch();
  // TODO: add version here
  const intentsTableResult = useIntentsTable();
  const { activity } = useActivity();
  const { intents, nlp, reassignment } = getNlpEvaluationFromInteraction(intentsTableResult.intents, activity);
  const [errorMessage, setErrorMsg] = useState('');
  const [notifyMessage, setNotification] = useState('');
  const [lastSelection, setSelection] = useState('');

  const copyPaste = text => {
    navigator.clipboard.writeText(text);
    setNotification('Copied!');
    setTimeout(() => {
      setNotification();
    }, 1000);
  };

  const intentGroupSelection = event => {
    event.preventDefault();
    const selected = event.target.value;
    setErrorMsg();
    setSelection(selected);
  };

  const cancelModal = () => {
    setErrorMsg();
    setSelection();
    settings.onCancel();
  };

  const validateOk = selection => {
    if (!selection) {
      setErrorMsg(labels.error);
      return;
    }
    setErrorMsg('');
    setSelection('');
    settings.onOk({
      input: settings.message,
      newAssignment: selection,
      prevAssignment: reassignment ? reassignment.newAssignment : settings.botAssignment,
      activityId: settings.activityId,
    });
  };

  return (
    <>
      <Divider />
      <div className="reassign-modal-content">
        {/*
        TODO: get copies for every case
        <Row>
          <Col span={24} className="bold">
            Type of error: false positive
          </Col>
        </Row>
        <Row>
          <Col span={24}>description of error...</Col>
        </Row>
        */}
        <Row style={{ alignItems: 'flex-end' }}>
          <Col span={3}>{labels.message}</Col>
          <Col span={13}>
            <span className="bold" style={{ lineHeight: '16px' }}>
              {settings.message}
            </span>
          </Col>
          <Col span={2} className="flex column">
            <Button
              aria-label={labels.btnCopy}
              style={{ marginRight: '10px' }}
              onClick={() => copyPaste(settings.message)}
            >
              <SvgIconCopyPaste title={labels.btnCopy} />
            </Button>
            <p>
              <em style={{ color: '#757575', position: 'absolute', top: '46px' }}>{notifyMessage}</em>
            </p>
          </Col>
          <Col span={6}>
            <Button
              type="primary"
              onClick={() => settings.dismissAndGoTo(ROUTES.manageBot.newIntent.path)}
              className="uppercase"
            >
              {labels.btnCreateNewIntent}
            </Button>
          </Col>
        </Row>

        <Spin spinning={!nlp} size="small" delay={500}>
          <Row>
            <Col span={6}>{labels.assignMessage}</Col>
            <Col span={18}>
              {nlp && (
                <span className="bold">
                  {nlp.intent.toLowerCase() === NONE_INTENT ? (
                    <>{nlp.intent}</>
                  ) : (
                    <a href={`/manage-the-bot/intents/${nlp.intent}/name?locale=${nlp.locale}`}>
                      {nlp.locale}.{nlp.intent}
                    </a>
                  )}
                </span>
              )}
            </Col>
          </Row>
        </Spin>

        {reassignment && reassignment.prevAssignment && reassignment.prevAssignment !== nlp.intent && (
          <Row>
            <Col span={6}>{labels.previouslyAssigned}</Col>
            <Col span={18}>
              <span className="bold">{reassignment.prevAssignment}</span>
            </Col>
          </Row>
        )}

        {reassignment && reassignment.newAssignment && (
          <Row>
            <Col span={6}>{labels.currentlyAssigned}</Col>
            <Col span={18}>
              <span className="bold">{reassignment.newAssignment}</span>
              <span>{` ${labels.by} `}</span>
              <span className="bold">{reassignment.modifiedBy}</span>
              <span>{` ${labels.on} ${formatDateTime(reassignment.updatedAt)}`}</span>
            </Col>
          </Row>
        )}

        <Row>
          <Col span={5}>{labels.assignTo}</Col>
          <Col span={19}>
            <DSSearch labelSearch={labels.searchPlaceholder} minSearchCharacters={0} onSubmitSearch={searchIntents} />
          </Col>
        </Row>

        <Row>
          <Col span={5}>{labels.currenIntents}</Col>
          <Col span={19}>
            <Radio.Group defaultValue={lastSelection} onChange={intentGroupSelection}>
              <Table
                locale={getCustomNoData()}
                columns={intentColumns}
                loading={!intents.length}
                dataSource={intents}
                pagination={paginationWrapper}
                rowKey={record => record[idField]}
              />
            </Radio.Group>
          </Col>
        </Row>
      </div>

      <div className="reassign-error">{errorMessage}</div>
      <Divider />
      <div className="reassign-modal-footer-btns">
        <Button ghost onClick={cancelModal} className="uppercase">
          {settings.cancelText}
        </Button>
        <Button onClick={() => validateOk(lastSelection)} type="primary" className="uppercase ok-button">
          {settings.okText}
        </Button>
      </div>
      <style jsx>{ModalStyles}</style>
    </>
  );
};

IntentReassignContent.propTypes = {
  settings: PropTypes.object,
};

export { IntentReassignContent };
